import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { Check, Copy, ExternalLink, FileText, ImageIcon, Landmark, Loader2 } from 'lucide-react';
import * as React from 'react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { checkFile, FileDropzone, type FileRules } from '@/components/ui/dropzone';
import { coinsApi } from '@/lib/api/endpoints';
import type { BankAccount, LedgerReason, Purchase, PurchaseStatus } from '@/lib/api/types';
import { cn } from '@/lib/utils';
import type { Tone } from '@/lib/visa';
import { userMessage } from '@/lib/api/errors';

export const PURCHASE_TONE: Record<PurchaseStatus, Tone> = { pending: 'info', approved: 'success', declined: 'danger' };
export const PURCHASE_LABEL: Record<PurchaseStatus, string> = { pending: 'Pending review', approved: 'Approved', declined: 'Declined' };

export function PurchaseBadge({ status }: { status: PurchaseStatus }) {
  return (
    <Badge tone={PURCHASE_TONE[status]} dot pulse={status === 'pending'}>
      {PURCHASE_LABEL[status]}
    </Badge>
  );
}

export const REASON_LABEL: Record<LedgerReason, string> = {
  signup_bonus: 'Sign-up bonus',
  purchase: 'Coin purchase',
  check: 'Visa check',
  refund: 'Refund (check failed)',
  admin_adjust: 'Adjusted by admin',
};

export function CopyButton({ value, label }: { value: string; label?: string }) {
  const [copied, setCopied] = React.useState(false);
  return (
    <button
      type="button"
      onClick={async (e) => {
        e.stopPropagation();
        try {
          await navigator.clipboard.writeText(value);
          setCopied(true);
          toast.success(`${label ?? 'Value'} copied`);
          setTimeout(() => setCopied(false), 1500);
        } catch {
          toast.error('Could not copy');
        }
      }}
      className="inline-flex size-7 items-center justify-center rounded-md text-muted-foreground transition hover:bg-secondary hover:text-foreground"
      title={`Copy ${label ?? ''}`}
    >
      {copied ? <Check className="size-3.5 text-emerald-500" /> : <Copy className="size-3.5" />}
    </button>
  );
}

/** Selectable bank-account card showing where to transfer. */
export function BankCard({ account, selected, onSelect }: { account: BankAccount; selected?: boolean; onSelect?: () => void }) {
  return (
    <motion.div
      role={onSelect ? 'radio' : undefined}
      aria-checked={onSelect ? selected : undefined}
      tabIndex={onSelect ? 0 : undefined}
      onClick={onSelect}
      onKeyDown={(e) => {
        if (onSelect && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          onSelect();
        }
      }}
      whileHover={onSelect ? { y: -2 } : undefined}
      className={cn(
        'relative overflow-hidden rounded-xl border p-4 transition-all',
        onSelect && 'cursor-pointer',
        selected ? 'border-primary bg-primary/[0.04] shadow-glow' : 'hover:border-primary/40',
      )}
    >
      {selected && (
        <motion.span layoutId="bank-selected" className="absolute right-3 top-3 flex size-6 items-center justify-center rounded-full bg-primary text-primary-foreground">
          <Check className="size-3.5" strokeWidth={3} />
        </motion.span>
      )}
      <div className="flex items-center gap-3">
        <span className="flex size-10 items-center justify-center rounded-xl bg-gradient-to-br from-brand-sun/20 to-brand-navy/20 text-primary-ink">
          <Landmark className="size-5" />
        </span>
        <div className="min-w-0 pr-6">
          <p className="truncate font-semibold">{account.bank_name}</p>
          <p className="truncate text-xs text-muted-foreground">{account.account_name}</p>
        </div>
      </div>
      <div className="mt-3 flex items-center justify-between rounded-lg bg-secondary/60 py-1.5 pl-3 pr-1">
        <span className="font-mono text-sm font-semibold tracking-wider">{account.account_number}</span>
        <CopyButton value={account.account_number} label="Account number" />
      </div>
    </motion.div>
  );
}

export const RECEIPT_RULES: FileRules = {
  accept: ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'],
  maxBytes: 5 * 1024 * 1024,
  typeMessage: 'Use a JPEG, PNG, WEBP or PDF file.',
};

export function checkReceiptFile(file: File): string | null {
  return checkFile(file, RECEIPT_RULES);
}

export function ReceiptDropzone({ file, onFile, error }: { file: File | null; onFile: (f: File | null) => void; error?: string }) {
  return (
    <FileDropzone
      file={file}
      onFile={onFile}
      error={error}
      accept={RECEIPT_RULES.accept}
      title="Drop your receipt here, or click to browse"
      hint="JPEG, PNG, WEBP or PDF · up to 5 MB"
    />
  );
}

/** Fetches the receipt with the Bearer token and shows it (image inline, PDF in an iframe). */
export function ReceiptViewer({ purchase, open, onOpenChange }: { purchase: Purchase; open: boolean; onOpenChange: (v: boolean) => void }) {
  const q = useQuery({
    queryKey: ['receipt', purchase.id],
    queryFn: async () => {
      const blob = await coinsApi.receipt(purchase.receipt_url);
      return { url: URL.createObjectURL(blob), type: blob.type };
    },
    enabled: open,
    staleTime: Infinity,
    gcTime: 5 * 60_000,
  });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent wide>
        <DialogHeader>
          <DialogTitle>Payment receipt</DialogTitle>
          <DialogDescription>
            {purchase.amount} {purchase.currency} to {purchase.bank_account.bank_name}
            {purchase.transaction_ref ? ` · Ref ${purchase.transaction_ref}` : ''}
          </DialogDescription>
        </DialogHeader>
        <div className="flex min-h-[300px] items-center justify-center overflow-hidden rounded-xl border bg-secondary/40">
          {q.isPending ? (
            <Loader2 className="size-6 animate-spin text-muted-foreground" />
          ) : q.isError ? (
            <p className="p-6 text-sm text-muted-foreground">{userMessage(q.error)}</p>
          ) : q.data.type === 'application/pdf' ? (
            <iframe title="Receipt" src={q.data.url} className="h-[65vh] w-full bg-white" />
          ) : (
            <img src={q.data.url} alt="Receipt" className="max-h-[65vh] w-auto object-contain" />
          )}
        </div>
        {q.data && (
          <Button asChild variant="outline" className="justify-self-end">
            <a href={q.data.url} target="_blank" rel="noreferrer">
              {q.data.type === 'application/pdf' ? <FileText /> : <ImageIcon />} Open in new tab <ExternalLink />
            </a>
          </Button>
        )}
      </DialogContent>
    </Dialog>
  );
}
